import React from 'react';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoSenha from '@/components/Formulario/CampoSenha';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import Grid, { GridItem } from '@/components/layout/Grid';
import AlertBox from '@/components/layout/AlertBox';
import { Accordion } from '@/components/layout/Accordion';
import VBox from '@/components/layout/VBox';
import { SquidConfigDTO } from '@webip/shared/src/dto/appliance';

type Props = {
    value: Partial<SquidConfigDTO>;
    onChange: (v: Partial<SquidConfigDTO>) => void;
    fieldErrors?: Record<string, string>;
};

const tipoOptions = [
    { id: 'container_mk', label: 'Container no MikroTik' },
    { id: 'vps', label: 'VPS externo' },
];

const sslModeOptions = [
    { id: 'bump', label: 'SSL Bump (decripta HTTPS, obtém URL + conteúdo, precisa de CA nos dispositivos)' },
    { id: 'peek', label: 'SNI Peek (não decripta, obtém domínio para filtragem apenas, não precisa de CA)' },
];

export const FormSquid = ({ value, onChange, fieldErrors = {} }: Props) => {
    const isContainerMk = value.tipo === 'container_mk' || !value.tipo;

    const update = (patch: Partial<SquidConfigDTO>) => onChange({ ...value, ...patch });

    return (
        <VBox>
            <Grid>
                <GridItem md={isContainerMk ? 6 : 12}>
                    <CampoSeletor
                        label="Tipo de instalação"
                        value={value.tipo ?? 'container_mk'}
                        options={tipoOptions}
                        onChange={(v) => update({ tipo: v as SquidConfigDTO['tipo'] })}
                        error={fieldErrors.tipo}
                    />
                </GridItem>

                {isContainerMk && (
                    <GridItem md={6}>
                        <CampoTexto
                            label="Nome do container no RouterOS"
                            value={value.container_nome ?? ''}
                            onChange={(v) => update({ container_nome: v })}
                            error={fieldErrors.container_nome}
                            placeholder="webip-squid"
                            autoComplete="off"
                            tooltipText='Nome do container conforme cadastrado no RouterOS (/container print). Usado para executar "squid -k reconfigure" dentro do container após cada sincronismo.'
                        />
                    </GridItem>
                )}

                <GridItem md={3}>
                    <CampoTexto
                        label="Endereço IP do Squid"
                        value={value.ip ?? ''}
                        onChange={(v) => update({ ip: v })}
                        error={fieldErrors.ip}
                        placeholder="192.168.1.x"
                    />
                </GridItem>

                <GridItem md={3}>
                    <CampoNumero
                        label="Porta SSH"
                        value={value.ssh_porta ?? 22}
                        onChange={(v) => update({ ssh_porta: v })}
                        error={fieldErrors.ssh_porta}
                    />
                </GridItem>

                <GridItem md={3}>
                    <CampoTexto
                        label="Usuário SSH"
                        value={value.ssh_usuario ?? ''}
                        onChange={(v) => update({ ssh_usuario: v })}
                        error={fieldErrors.ssh_usuario}
                        placeholder={isContainerMk ? 'root' : ''}
                        autoComplete="off"
                    />
                </GridItem>

                <GridItem md={3}>
                    <CampoSenha
                        label="Senha SSH"
                        value={value.ssh_senha ?? ''}
                        onChange={(v) => update({ ssh_senha: v })}
                        error={fieldErrors.ssh_senha}
                        placeholder="Deixe em branco para manter a atual"
                        autoComplete="new-password"
                    />
                </GridItem>

                <GridItem md={6}>
                    <CampoTexto
                        label={isContainerMk
                            ? 'Caminho do squid.conf no volume externo (MikroTik)'
                            : 'Caminho do arquivo de configuração'}
                        value={value.conf_path ?? ''}
                        onChange={(v) => update({ conf_path: v })}
                        error={fieldErrors.conf_path}
                        placeholder={isContainerMk
                            ? '/usb1/squid/conf.d/webip-managed.conf'
                            : '/etc/squid/conf.d/webip-managed.conf'}
                        autoComplete="off"
                    />
                </GridItem>

                <GridItem md={3}>
                    <CampoNumero
                        label="Porta HTTP"
                        value={value.porta ?? 3128}
                        onChange={(v) => update({ porta: v })}
                        error={fieldErrors.porta}
                    />
                </GridItem>

                <GridItem md={3}>
                    <CampoNumero
                        label="Porta HTTPS"
                        value={value.https_porta ?? 3129}
                        onChange={(v) => update({ https_porta: v })}
                        error={fieldErrors.https_porta}
                    />
                </GridItem>

                <GridItem md={12}>
                    <CampoSeletor
                        label="Modo HTTPS"
                        value={value.ssl_mode ?? 'bump'}
                        options={sslModeOptions}
                        onChange={(v) => update({ ssl_mode: v as SquidConfigDTO['ssl_mode'] })}
                        error={fieldErrors.ssl_mode}
                        tooltipText='Define como os domínios são casados em tráfego HTTPS. "SSL Bump" decripta a conexão e usa a mesma ACL do HTTP (dstdomain) — exige que o certificado da CA do Squid seja confiável nos dispositivos. "SNI Peek" apenas lê o nome do servidor (SNI) sem decriptar, usando ssl::server_name — não exige instalar certificado, mas só bloqueia por domínio, não por caminho da URL.'
                    />
                </GridItem>

                <GridItem md={12}>
                    <CampoCheckbox
                        label="Squid ativo"
                        text="Ativar o serviço Squid para filtrar tráfego HTTP/HTTPS"
                        value={value.ativo ?? true}
                        onChange={(v) => update({ ativo: v })}
                    />
                </GridItem>
            </Grid>

            {isContainerMk && (
                <Accordion title="Como instalar o Squid no container MikroTik">
                    <VBox style={{ gap: 8, padding: '8px 0' }}>
                        <AlertBox variant="info">
                            A instalação do container Squid no MikroTik deve ser feita manualmente pelo
                            administrador. Consulte a documentação oficial do RouterOS sobre containers.
                        </AlertBox>
                        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
                            <strong>Passos resumidos:</strong>
                        </p>
                        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
                            <li>
                                Habilite a feature <code>container</code> no RouterOS:{' '}
                                <code>/system/device-mode/update container=yes</code>
                            </li>
                            <li>
                                Crie uma interface veth para o container e atribua um IP na rede LAN.
                            </li>
                            <li>
                                Faça pull da imagem Squid:{' '}
                                <code>/container/add remote-image=ubuntu/squid interface=veth1 ...</code>
                            </li>
                            <li>
                                Configure o Squid para escutar na porta <strong>3128</strong> e aceitar
                                conexões da rede interna.
                            </li>
                            <li>
                                Crie o diretório <code>/etc/squid/conf.d/</code> no container —
                                o WebIP irá gravar o arquivo <code>webip-managed.conf</code> via SSH
                                durante o sincronismo.
                            </li>
                            <li>
                                Informe o IP do container e as credenciais SSH neste formulário.
                            </li>
                        </ol>
                        <AlertBox variant="warning">
                            As credenciais SSH são usadas apenas internamente pelo agente para aplicar
                            configurações. Nunca são retornadas pela API.
                        </AlertBox>
                    </VBox>
                </Accordion>
            )}
        </VBox>
    );
};

export default FormSquid;
